/**
 * Edit date / modality / duration of an engagement session (AI-observed or
 * manual) without touching the observation form ratings.
 *
 * Reuses the `ManualSessionModal` header fields; PATCHes only the session
 * metadata so AI ratings are never clobbered.
 */
import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';

import { useTranslation } from '@/hooks/useTranslation';
import type { EngagementSessionRow, SessionModality, SessionUpdateRequest } from '@/types/mentorEngagements';
import { ActionButton, SegmentedField, TextField, mentorStyles } from './shared';
import { WorkspaceModal } from './WorkspaceModal';

const MODALITIES: SessionModality[] = ['in_person', 'virtual', 'recorded'];

export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

interface SessionMetaModalProps {
  session: EngagementSessionRow | null;
  onClose: () => void;
  onSave: (session: EngagementSessionRow, payload: SessionUpdateRequest) => Promise<void>;
}

export function SessionMetaModal({ session, onClose, onSave }: SessionMetaModalProps) {
  const { t } = useTranslation();
  const [sessionDate, setSessionDate] = useState('');
  const [modality, setModality] = useState<SessionModality>('virtual');
  const [duration, setDuration] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!session) return;
    setSessionDate(session.session_date || '');
    setModality(session.modality || 'virtual');
    setDuration(session.duration_minutes ? String(session.duration_minutes) : '');
    setError(null);
  }, [session]);

  const submit = async () => {
    if (!session || saving) return;
    if (!isIsoDate(sessionDate)) {
      setError(t('mentorEngagements.manual.invalidDate'));
      return;
    }
    if (!/^\d+$/.test(duration.trim()) || Number(duration) <= 0) {
      setError(t('mentorEngagements.manual.invalidDuration'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(session, { session_date: sessionDate, modality, duration_minutes: Number(duration) });
      onClose();
    } catch {
      setError(t('mentorEngagements.sessionMeta.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceModal
      testID="session_meta_modal"
      visible={session !== null}
      busy={saving}
      title={t('mentorEngagements.sessionMeta.title')}
      subtitle={t('mentorEngagements.sessionMeta.subtitle')}
      onClose={onClose}
      footer={(
        <>
          <ActionButton label={t('mentorEngagements.sessionMeta.cancel')} onPress={onClose} disabled={saving} />
          <ActionButton
            testID="session_meta_save"
            label={t('mentorEngagements.sessionMeta.save')}
            onPress={submit}
            variant="primary"
            disabled={saving}
          />
        </>
      )}
    >
      <View style={mentorStyles.row}>
        <TextField
          testID="session_meta_date"
          editable={!saving}
          label={t('mentorEngagements.manual.sessionDate')}
          value={sessionDate}
          onChangeText={setSessionDate}
          placeholder="YYYY-MM-DD"
        />
        <TextField
          testID="session_meta_duration"
          editable={!saving}
          label={t('mentorEngagements.manual.duration')}
          value={duration}
          onChangeText={setDuration}
          keyboardType="numeric"
        />
      </View>
      <SegmentedField<SessionModality>
        testID="session_meta_modality"
        label={t('mentorEngagements.manual.modality')}
        options={MODALITIES.map((value) => ({ value, label: t(`mentorEngagements.modality.${value}`) }))}
        value={modality}
        disabled={saving}
        onChange={setModality}
      />
      {error ? <Text accessibilityRole="alert" style={mentorStyles.errorText}>{error}</Text> : null}
    </WorkspaceModal>
  );
}

export default SessionMetaModal;
